// src/talent/RoleAlerts.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/api";
import { ENDPOINTS } from "../../api/endpoints";

export default function RoleAlerts({ userId, showToast, onProfileDefaultsStatus }) {
  const [loading, setLoading] = useState(true);
  const [activeCell, setActiveCell] = useState(true);
  const [confirmOptOutOpen, setConfirmOptOutOpen] = useState(false);
  const [usingProfileDefaults, setUsingProfileDefaults] = useState(false);

  /* ---------------- BASIC VALIDATION HELPERS ---------------- */
  const isValidEmail = (email) => {
    if (!email) return true;
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
  };

  const isMatchingEmail = (email1, email2) => {
    if (!email1 || !email2) return true;
    return email1.trim() === email2.trim();
  };

  const isValidPhone = (num) => {
    if (!num) return true;
    return /^[0-9]{10}$/.test(num);
  };

  /* ---------------- UI VALIDATION ERRORS ---------------- */
  const [uiErrors, setUiErrors] = useState({
    minAge: "",
    maxAge: "",
  });

  /* ---------------- DROPDOWN OPTIONS ---------------- */
  const [options, setOptions] = useState({
    locations: [],
    jobCategories: [],
    ethnicities: [],
    unions: [],
    roleTypes: [],
    paymentTypes: [],
    genders: [
      "Any",
      "Male",
      "Female",
      "Trans Male",
      "Trans Female",
      "Non-Binary",
    ],
  });

  /* ---------------- USER PREFS ---------------- */
  const [prefs, setPrefs] = useState({
    userEmail: "",
    userEmail2: "",
    userCell: "",
    cellProvider: "",
    jobLocations: [],
    jobCategories: [],
    ethnicity: [],
    unions: [],
    roleType: [],
    payType: [],
    sex: [],
    minAge: "",
    maxAge: "",
  });

  /* ---------------- UPDATE HELPER ---------------- */
  const update = (field, value) => {
    const numericFields = ["minAge", "maxAge"];

    if (numericFields.includes(field)) {
      if (!/^[0-9]*$/.test(value)) return;
    }

    setPrefs((prev) => ({ ...prev, [field]: value }));
  };

  const resolveSingleAny = (value, allValues) => {
    if (!value || value === "Any") return allValues.join(",");
    return value;
  };

  const resolveMulti = (selected, allValues) => {
    return selected.length === 0 ? allValues.join(",") : selected.join(",");
  };

  const ROLE_TYPE_OPTIONS = [
    "Principal",
    "Supporting",
    "Co-Star",
    "Day Player",
    "Voice-Over",
    "Stunts",
    "Chorus-Ensemble",
    "Special Ability",
    "Precision Driver",
    "Stand-In",
    "Photo Double",
    "Extras",
    "N/A",
  ];

  /* ---------------- UI LIVE VALIDATION ---------------- */
  useEffect(() => {
    const errors = {
      minAge: "",
      maxAge: "",
    };

    const minAge = Number(prefs.minAge || 0);
    const maxAge = Number(prefs.maxAge || 0);

    if (prefs.minAge && prefs.maxAge && minAge > maxAge)
      errors.maxAge = "Maximum age must be greater than minimum age";

    if (prefs.maxAge && maxAge > 150)
      errors.maxAge = "Maximum age cannot exceed 150";

    setUiErrors(errors);
  }, [prefs]);

  const fieldSx = {
    "& .MuiInputLabel-root": {
      color: "#90b5fe",
    },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      backgroundColor: "#202020", // match your page background
      paddingLeft: "4px",
      paddingRight: "4px",
      borderRadius: "2px",
      fontSize: "16px",
    },
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#f5f5f5",
      borderRadius: "8px",
      height: "44px",
    },
  };

  /* ---------------- PROFILE -> ROLE ALERT DEFAULTS ----------------
   * Builds a set of Role Alert field defaults from the Talent Profile API,
   * for fields that overlap between the two: contact info, ethnicity,
   * gender, age range, and location. For fields that have NO equivalent
   * on the Talent Profile (job categories, unions, role type, pay type),
   * we default to "select all" / "Any" so a first-time alert doesn't
   * silently exclude anything the user never chose to filter out.
   *
   * NOTE on field-name assumptions (please verify against your real API
   * responses and adjust if the shapes differ):
   *  - Profile: email, primaryContactNumber, ethnicityCode (id), sex,
   *             ageRangeFrom, ageRangeTo, location (state name), city
   *  - Locations dropdown (GetLocations): locationState, locationCity,
   *             locationCode, locationStateAbbreviatoion
   *  - Ethnicities dropdown (get-all-ethnicities): ethnicityId, ethnicityName
   * ------------------------------------------------------------------ */
  // Matches a profile's state against the Role Alerts location list. Loose
  // match on state only (no city). Tries the state abbreviation first
  // (profile.stateCode vs. locationStateAbbreviatoion); falls back to a
  // full state-name match (profile.location vs. locationState) in case the
  // abbreviation field is missing/inconsistent on some records. If a state
  // has multiple city-level codes (CA1/CA2/CA3, etc.), all of them prefill.
  const matchProfileLocations = (profile, locOptions) => {
    const stateAbbr = (profile?.stateCode || "").trim().toUpperCase();
    const stateName = (profile?.location || "").trim().toLowerCase();

    if (!stateAbbr && !stateName) return [];

    return locOptions
      .filter((l) => {
        const optAbbr = (l.stateAbbr || "").trim().toUpperCase();
        const optName = (l.stateRaw || "").trim().toLowerCase();
        return (
          (stateAbbr && optAbbr === stateAbbr) ||
          (stateName && optName === stateName)
        );
      })
      .map((l) => l.value);
  };

  const buildProfileDefaults = (
    profile,
    ethnicityOptions,
    locOptions,
    categoryOptions,
    unionOptions,
    payOptions,
  ) => {
    if (!profile) return null;

    // Ethnicity: the profile gives both a readable name (profile.ethnicity)
    // and an id (profile.ethnicityCode). Prefer matching by name since it's
    // more directly comparable to Role Alerts' name-keyed list; fall back
    // to id if the name doesn't line up for some reason.
    const matchedEthnicity =
      ethnicityOptions.find(
        (e) =>
          e.value.trim().toLowerCase() ===
          (profile.ethnicity || "").trim().toLowerCase(),
      ) ||
      ethnicityOptions.find((e) => String(e.id) === String(profile.ethnicityCode));

      const matchedUnions = (profile.affiliations || "")
  .split(",")
  .map((u) => u.trim())
  .filter(Boolean)
  .map((affiliation) =>
    unionOptions.find(
      (u) =>
        u.value.trim().toLowerCase() === affiliation.toLowerCase()
    )
  )
  .filter(Boolean)
  .map((u) => u.value);
    // Location: loose match on state (no city). See matchProfileLocations
    // for the abbreviation/full-name fallback logic.
    const matchedLocations = matchProfileLocations(profile, locOptions);
    if (!matchedLocations.length && (profile.stateCode || profile.location)) {
      // Helps diagnose a state that didn't match anything in GetLocations -
      // safe to remove once location prefill is confirmed working.
      console.warn(
        "Role Alerts: no location match for profile state",
        profile.stateCode,
        profile.location,
      );
    }

    return {
      userEmail: profile.email || "",
      userEmail2: profile.email || "",
      userCell: profile.primaryContactNumber || "",
      ethnicity: matchedEthnicity ? [matchedEthnicity.value] : [],
      sex: profile.sex ? [profile.sex] : [],
      minAge:
        profile.ageRangeFrom !== null && profile.ageRangeFrom !== undefined
          ? String(profile.ageRangeFrom)
          : "",
      maxAge:
        profile.ageRangeTo !== null && profile.ageRangeTo !== undefined
          ? String(profile.ageRangeTo)
          : "",
      jobLocations: matchedLocations,

      // No equivalent field on the Talent Profile for job categories,
// role type, and pay type — default to "select all" / "Any".
// Union Status is populated from profile.affiliations when available.
      jobCategories: categoryOptions.map((c) => c.value),
unions:
  matchedUnions.length > 0
    ? matchedUnions
    : ["Any", ...unionOptions.map((u) => u.value)],
      roleType: ["Any", ...ROLE_TYPE_OPTIONS],
      payType: ["Any", ...payOptions.map((p) => String(p.value))],
    };
  };

  /* ---------------- LOAD DROPDOWNS ---------------- */
  const loadDropdowns = async () => {
    try {
      // Core dropdown data — these six must all succeed for the form to be
      // usable, so they stay in one Promise.all.
      const [locRes, categoryRes, ethnicityRes, unionRes, roleRes, payRes] =
        await Promise.all([
          api.get(ENDPOINTS.getLocations()),
          api.get(ENDPOINTS.getJobCategories()),
          api.get(ENDPOINTS.getEthnicities()),
          api.get(ENDPOINTS.getUnions()),
          api.get(ENDPOINTS.getAllRoles()),
          api.get(ENDPOINTS.getPayTypes()),
        ]);

      const locOptions =
        locRes?.data?.data?.map((loc) => ({
          label:
            loc.locationCity &&
            loc.locationCity.trim() !== "" &&
            loc.locationCity !== loc.locationState
              ? `${loc.locationState} (${loc.locationCity})`
              : loc.locationState,
          value: loc.locationCode,
          // kept for matching against the profile's city/state, not shown in UI
          stateRaw: loc.locationState,
          stateAbbr: loc.locationStateAbbreviatoion,
          cityRaw: loc.locationCity,
        })) || [];

      // Float "Nationwide" (locationCode "NW") to the top of the list
      locOptions.sort((a, b) => {
        const aIsNationwide = a.value === "NW";
        const bIsNationwide = b.value === "NW";
        if (aIsNationwide && !bIsNationwide) return -1;
        if (!aIsNationwide && bIsNationwide) return 1;
        return 0;
      });

      const categoryOptions =
        categoryRes?.data?.data
          ?.filter((c) => c.activeFlag)
          .map((c) => ({
            label: c.jobCategoryName,
            value: c.jobCategoryName,
          })) || [];

      const ethnicityOptions =
        ethnicityRes?.data?.data?.map((e) => ({
          label: e.ethnicityName,
          value: e.ethnicityName,
          // kept for matching against the profile's ethnicityCode, not shown in UI
          id: e.ethnicityId,
        })) || [];

      const unionOptions =
        unionRes?.data?.map((u) => ({
          label: u.affiliationName,
          value: u.affiliationName,
        })) || [];

      const roleOptions =
        roleRes?.data?.data?.map((r) => ({
          label: r.roleName,
          value: r.roleName,
        })) || [];

      const payOptions =
        payRes?.data?.data?.map((p) => ({
          label: p.payType,
          value: p.payId,
        })) || [];

      setOptions({
        locations: locOptions,
        jobCategories: categoryOptions,
        ethnicities: ethnicityOptions,
        unions: unionOptions,
        roleTypes: roleOptions,
        paymentTypes: payOptions,
        genders: options.genders,
      });

      // Profile fetch is isolated from the Promise.all above on purpose:
      // if the profile endpoint 404s, times out, or the endpoint isn't
      // wired up yet, that should NOT take down the dropdowns or the
      // saved-preferences load below it.
      let profile = null;
      try {
        const profileRes = await api.get(
          ENDPOINTS.getTalentProfileDetails({ userId }),
        );
        profile = profileRes?.data?.data || profileRes?.data || null;
      } catch (err) {
        console.error(
          "Role Alerts: profile fetch failed, continuing without defaults:",
          err,
        );
      }

      const profileDefaults = buildProfileDefaults(
        profile,
        ethnicityOptions,
        locOptions,
        categoryOptions,
        unionOptions,
        payOptions,
      );

      await loadUserPrefs(unionOptions, payOptions, profileDefaults);
    } catch (err) {
      console.error("Dropdown load error:", err);
    }
  };

  /* ---------------- LOAD EXISTING PREFS ---------------- */
  const loadUserPrefs = async (
    unionOptions = [],
    payOptions = [],
    profileDefaults = null,
  ) => {
    try {
      const res = await api.get(ENDPOINTS.getUserNotificationPrefs({ userId }));
      const p = res?.data?.[0];

      if (!p) {
        // No saved Role Alert preferences yet — prefill from the Talent
        // Profile instead of leaving the form blank.
        if (profileDefaults) {
          setPrefs((prev) => ({ ...prev, ...profileDefaults }));
          setUsingProfileDefaults(true);
          onProfileDefaultsStatus?.(true);
        }
        return;
      }

      setActiveCell(p.activeCell);

      // If the user has opted out, never trust the API's contact/preference
      // fields (some backends fall back to profile email/cell when there's
      // no active alert row) — force the form back to a clean, blank state.
      if (!p.activeCell) {
        clearRoleAlertFields();
        return;
      }

      setUsingProfileDefaults(false);
      onProfileDefaultsStatus?.(false);

      setPrefs({
        userEmail: p.userEmail || "",
        userEmail2: p.userEmail2 || "",
        userCell: p.userCell || "",
        cellProvider: p.cellProvider || "",

        jobLocations: p.cityChoice
          ? p.cityChoice.split(",").filter((v) => v !== "string")
          : [],
        jobCategories: p.jobCategories
          ? p.jobCategories.split(",").filter((v) => v !== "string")
          : [],

        ethnicity: p.ethnicity ? p.ethnicity.split(",").filter(Boolean) : [],
        unions:
          p.unionStatus === unionOptions.map((u) => u.value).join(",")
            ? ["Any", ...unionOptions.map((u) => u.value)]
            : (p.unionStatus?.split(",").filter(Boolean) || []),

        roleType:
          p.roleType === ROLE_TYPE_OPTIONS.join(",")
            ? ["Any", ...ROLE_TYPE_OPTIONS]
            : (p.roleType?.split(",").filter(Boolean) || []),

        payType:
          p.strPay === payOptions.map((p) => String(p.value)).join(",")
            ? ["Any", ...payOptions.map((p) => String(p.value))]
            : (p.strPay?.split(",").filter(Boolean) || []),
        sex:
          p.userSex === options.genders.filter((g) => g !== "Any").join(",")
            ? ["Any", ...options.genders.filter((g) => g !== "Any")]
            : (p.userSex?.split(",").filter(Boolean) || []),

        minAge: p.minAge?.toString() || "",
        maxAge: p.maxAge?.toString() || "",
      });
    } catch (err) {
      console.error("Error loading prefs:", err);
    }
  };

  const clearForm = () => {
    setPrefs({
      userEmail: "",
      userEmail2: "",
      userCell: "",
      cellProvider: "",

      jobLocations: [],
      jobCategories: [],
      ethnicity: [],
      unions: [],

      roleType: [],
      payType: [],
      sex: [],

      minAge: "",
      maxAge: "",
    });
  };

  const clearRoleAlertFields = () => {
    setPrefs({
      userEmail: "",
      userEmail2: "",
      userCell: "",
      cellProvider: "",
      jobLocations: [],
      jobCategories: [],
      ethnicity: [],
      unions: [],
      roleType: [],
      payType: [],
      sex: [],
      minAge: "",
      maxAge: "",
    });
  };

  const handleJobCategoryChange = (value) => {
    if (value === "ALL") {
      const allValues = options.jobCategories.map((c) => c.value);

      const isAllSelected = prefs.jobCategories.length === allValues.length;

      update("jobCategories", isAllSelected ? [] : allValues);
      return;
    }

    const exists = prefs.jobCategories.includes(value);

    update(
      "jobCategories",
      exists
        ? prefs.jobCategories.filter((x) => x !== value)
        : [...prefs.jobCategories, value],
    );
  };

  /* ---------------- SAVE PREFS ---------------- */
  const savePrefs = async () => {
    if (!activeCell) {
      const payload = {
        userId,

        userMail: "",
        userMail2: "",
        userCell: "",
        cellProvider: "",

        jobCategories: "",
        cityChoice: "",

        ethnicity: "",
        unionStatus: "",

        roleType: "",
        payStatus: "",
        userSex: "",

        ageFrom: null,
        ageTo: null,

        activeStatus: 0,
        activeCell: false,
      };

      await api.post(ENDPOINTS.addOrUpdateUserNotificationPrefs(), payload);
      onProfileDefaultsStatus?.(false);
      showToast("You have successfully opted out of Role Alerts.");

      return;
    }
    if (!isValidEmail(prefs.userEmail))
      return showToast("Please enter a valid Main Email", "error");

    if (!isValidEmail(prefs.userEmail2))
      return showToast("Please enter a valid Repeat Email", "error");

    if (!isMatchingEmail(prefs.userEmail, prefs.userEmail2))
      return showToast("Both emails must match", "error");

    if (Object.values(uiErrors).some((msg) => msg !== "")) {
      return showToast("Please fix highlighted validation errors", "error");
    }
    // ---------------- MANDATORY FIELD VALIDATION ----------------

    // Email
    if (!prefs.userEmail) return showToast("Main Email is required", "error");

    if (!prefs.userEmail2)
      return showToast("Repeat Email is required", "error");

    // Job Locations
    if (prefs.jobLocations.length === 0)
      return showToast("Please select at least one Job Location", "error");

    // Job Categories
    if (prefs.jobCategories.length === 0)
      return showToast("Please select at least one Job Category", "error");

    // Ethnicity
    if (prefs.ethnicity.length === 0)
      return showToast("Please select at least one Ethnicity", "error");

    // Union
    if (prefs.unions.length === 0)
      return showToast("Please select at least one Union Status", "error");

    // Age
    if (!prefs.minAge) return showToast("Please enter minimum age", "error");

    if (!prefs.maxAge) return showToast("Please enter maximum age", "error");

    try {
      /* ---------------- MULTI SELECT (EMPTY = EMPTY) ---------------- */

      const jobCategoriesPayload =
        prefs.jobCategories.length === options.jobCategories.length
          ? options.jobCategories.map((c) => c.value).join(",")
          : prefs.jobCategories.join(",");
      const locationsPayload = prefs.jobLocations.join(",");
      const ethnicityPayload =
        prefs.ethnicity.length === 0
          ? options.ethnicities.map((e) => e.value).join(",")
          : prefs.ethnicity.join(",");
      const unionPayload =
        !prefs.unions.length || prefs.unions.includes("Any")
          ? options.unions.map((u) => u.value).join(",")
          : prefs.unions.filter((u) => u !== "Any").join(",");
      const roleTypePayload =
        !prefs.roleType.length || prefs.roleType.includes("Any")
          ? ROLE_TYPE_OPTIONS.join(",")
          : prefs.roleType.join(",");

      const genderPayload =
        !prefs.sex.length || prefs.sex.includes("Any")
          ? options.genders.filter((g) => g !== "Any").join(",")
          : prefs.sex.filter((g) => g !== "Any").join(",");
      const payTypePayload =
        !prefs.payType.length || prefs.payType.includes("Any")
          ? options.paymentTypes.map((p) => p.value).join(",")
          : prefs.payType.join(",");

      /* ---------------- FINAL PAYLOAD ---------------- */

      const payload = {
        userId,

        userMail: prefs.userEmail,
        userMail2: prefs.userEmail2,
        userCell: prefs.userCell,
        cellProvider: prefs.cellProvider,

        jobCategories: jobCategoriesPayload,
        cityChoice: locationsPayload,

        ethnicity: ethnicityPayload,
        unionStatus: unionPayload,

        roleType: roleTypePayload,
        payStatus: payTypePayload,
        userSex: genderPayload,

        ageFrom: Number(prefs.minAge || 0),
        ageTo: Number(prefs.maxAge || 0),

        activeStatus: 1,
        activeCell: true,
      };

      await api.post(ENDPOINTS.addOrUpdateUserNotificationPrefs(), payload);

      setUsingProfileDefaults(false);
      onProfileDefaultsStatus?.(false);

      showToast(
        "You’re all set! We’ll send you casting calls that match your preferences.",
      );
    } catch (err) {
      console.error("Save error:", err);
      showToast("Failed to save preferences", "error");
    }
  };
  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    (async () => {
      await loadDropdowns();
      setLoading(false);
    })();
  }, []);
  if (loading) return <Typography>Loading...</Typography>;
  /* ---------------- RENDER UI ---------------- */
  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
        Role Alerts
      </Typography>

      {/* INFO BOX */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          backgroundColor: "#90b5fe",
          borderRadius: 2,
          p: 2,
          color: "#fff",
          mb: 2,
        }}
      >
        {/* Left side */}
        <Box display="flex" gap={1.5}>
          <InfoOutlinedIcon sx={{ mt: "2px" }} />

          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
            <b>
              Set your preferences below to receive casting alerts by email.
            </b>
            <br />
            Choose your locations, role types, and categories.
            <br />
            Then click <b>“SAVE and Start Getting Casting Alerts”</b>
            <br />
            These alerts will come from{" "}
            <b style={{ color: "#fff", textDecoration: "underline" }}>
              alerts@directsubmit.com
            </b>
            . Please mark this email as a safe sender.
          </Typography>
        </Box>

        <IconButton size="small" sx={{ color: "#fff" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* EMAIL */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            disabled={!activeCell}
            label="Main Email"
            value={prefs.userEmail}
            onChange={(e) => update("userEmail", e.target.value)}
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            disabled={!activeCell}
            label="Repeat Email"
            value={prefs.userEmail2}
            onChange={(e) => update("userEmail2", e.target.value)}
            sx={fieldSx}
          />
        </Grid>

        {/* JOB LOCATIONS */}
        <Grid item xs={6}>
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel shrink>Job Locations</InputLabel>
            <Select
              multiple
              value={prefs.jobLocations}
              displayEmpty
              disabled={!activeCell}
              renderValue={(selected) => {
                if (!selected.length)
                  return (
                    <span style={{ color: "#888" }}>Select Locations</span>
                  );
                return options.locations
                  .filter((o) => selected.includes(o.value))
                  .map((o) => o.label)
                  .join(", ");
              }}
              onChange={(e) => update("jobLocations", e.target.value)}
            >
              {options.locations.map((loc) => (
                <MenuItem key={loc.value} value={loc.value}>
                  <Checkbox
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: "#90b5fe" },
                    }}
                    disabled={!activeCell}
                    checked={prefs.jobLocations.includes(loc.value)}
                  />
                  {loc.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* JOB CATEGORIES */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            sx={{ fontWeight: 700, color: "#fff", fontSize: "16px", mb: 1 }}
          >
            Job Categories
          </Typography>
          <Grid container>
            <Grid item xs={12} sm={4} md={3}>
              <FormControlLabel
                sx={{ "& .MuiTypography-root": { color: "#fff" } }}
                control={
                  <Checkbox
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: "#90b5fe" },
                    }}
                    checked={
                      prefs.jobCategories.length ===
                        options.jobCategories.length &&
                      options.jobCategories.length > 0
                    }
                    disabled={!activeCell}
                    onChange={() => handleJobCategoryChange("ALL")}
                  />
                }
                label="All"
              />
            </Grid>
            {options.jobCategories.map((cat) => (
              <Grid item xs={12} sm={4} md={3} key={cat.value}>
                <FormControlLabel
                  sx={{ "& .MuiTypography-root": { color: "#fff" } }}
                  control={
                    <Checkbox
                      sx={{
                        color: "#ccc",
                        "&.Mui-checked": { color: "#90b5fe" },
                      }}
                      checked={prefs.jobCategories.includes(cat.value)}
                      disabled={!activeCell}
                      onChange={() => handleJobCategoryChange(cat.value)}
                    />
                  }
                  label={cat.label}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* ETHNICITY */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            sx={{ fontWeight: 700, color: "#fff", fontSize: "16px", mb: 1 }}
          >
            Ethnicity
          </Typography>
          <Grid container>
            {options.ethnicities.map((e) => (
              <Grid item xs={12} sm={4} md={3} key={e.value}>
                <FormControlLabel
                  sx={{ "& .MuiTypography-root": { color: "#fff" } }}
                  control={
                    <Checkbox
                      sx={{
                        color: "#ccc",
                        "&.Mui-checked": { color: "#90b5fe" },
                      }}
                      disabled={!activeCell}
                      checked={prefs.ethnicity.includes(e.value)}
                      onChange={() => {
                        const exists = prefs.ethnicity.includes(e.value);
                        update(
                          "ethnicity",
                          exists
                            ? prefs.ethnicity.filter((x) => x !== e.value)
                            : [...prefs.ethnicity, e.value],
                        );
                      }}
                    />
                  }
                  label={e.label}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* UNION */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel shrink>Union Status</InputLabel>

            <Select
              multiple
              value={prefs.unions}
              displayEmpty
              disabled={!activeCell}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <span style={{ color: "#888" }}>Any</span>
                ) : (
                  selected.filter((v) => v !== "Any").join(", ") || "Any"
                )
              }
              onChange={(e) => {
                const incoming = e.target.value;
                const allUnions = options.unions.map((u) => u.value);

                if (incoming.includes("Any") && !prefs.unions.includes("Any")) {
                  update("unions", ["Any", ...allUnions]);
                } else if (
                  !incoming.includes("Any") &&
                  prefs.unions.includes("Any")
                ) {
                  update("unions", []);
                } else {
                  update(
                    "unions",
                    incoming.filter((v) => v !== "Any"),
                  );
                }
              }}
            >
              <MenuItem value="Any">
                <Checkbox
                  sx={{
                    color: "#ccc",
                    "&.Mui-checked": { color: "#90b5fe" },
                  }}
                  checked={prefs.unions.includes("Any")}
                  disabled={!activeCell}
                />
                Any
              </MenuItem>

              {options.unions.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  <Checkbox
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: "#90b5fe" },
                    }}
                    checked={prefs.unions.includes(u.value)}
                    disabled={!activeCell}
                  />
                  {u.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* AGE */}
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            disabled={!activeCell}
            label="Age From"
            value={prefs.minAge}
            onChange={(e) => update("minAge", e.target.value)}
            sx={fieldSx}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            disabled={!activeCell}
            label="Age To"
            value={prefs.maxAge}
            onChange={(e) => update("maxAge", e.target.value)}
            sx={fieldSx}
          />
        </Grid>

        {/* ROLE TYPE / GENDER / PAYMENT TYPE */}
        {/* ROLE TYPE */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel shrink>Role Type</InputLabel>
            <Select
              multiple
              value={prefs.roleType}
              disabled={!activeCell}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <span style={{ color: "#888" }}>Any</span>
                ) : (
                  selected.filter((v) => v !== "Any").join(", ") || "Any"
                )
              }
              onChange={(e) => {
                const incoming = e.target.value;
                // If "Any" just got selected → select all
                if (
                  incoming.includes("Any") &&
                  !prefs.roleType.includes("Any")
                ) {
                  update("roleType", ["Any", ...ROLE_TYPE_OPTIONS]);
                }
                // If "Any" just got deselected → clear all
                else if (
                  !incoming.includes("Any") &&
                  prefs.roleType.includes("Any")
                ) {
                  update("roleType", []);
                }
                // Normal toggle
                else {
                  update(
                    "roleType",
                    incoming.filter((v) => v !== "Any"),
                  );
                }
              }}
            >
              <MenuItem value="Any">
                <Checkbox
                  sx={{ color: "#ccc", "&.Mui-checked": { color: "#90b5fe" } }}
                  disabled={!activeCell}
                  checked={prefs.roleType.includes("Any")}
                />
                Any
              </MenuItem>
              {ROLE_TYPE_OPTIONS.map((role) => (
                <MenuItem key={role} value={role}>
                  <Checkbox
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: "#90b5fe" },
                    }}
                    disabled={!activeCell}
                    checked={prefs.roleType.includes(role)}
                  />
                  {role}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* GENDER */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel shrink>Gender</InputLabel>
            <Select
              multiple
              value={prefs.sex}
              disabled={!activeCell}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <span style={{ color: "#888" }}>Any</span>
                ) : (
                  selected.join(", ")
                )
              }
              onChange={(e) => {
                const incoming = e.target.value;
                const allGenders = options.genders.filter((g) => g !== "Any");
                // If "Any" just got selected → select all
                if (incoming.includes("Any") && !prefs.sex.includes("Any")) {
                  update("sex", ["Any", ...allGenders]);
                }
                // If "Any" just got deselected → clear all
                else if (
                  !incoming.includes("Any") &&
                  prefs.sex.includes("Any")
                ) {
                  update("sex", []);
                }
                // Normal toggle
                else {
                  update(
                    "sex",
                    incoming.filter((v) => v !== "Any"),
                  );
                }
              }}
            >
              {options.genders.map((g) => (
                <MenuItem key={g} value={g}>
                  <Checkbox
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: "#90b5fe" },
                    }}
                    disabled={!activeCell}
                    checked={prefs.sex.includes(g)}
                  />
                  {g}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* PAYMENT TYPE */}
        <Grid item xs={12} md={4}>
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel shrink>Payment Type</InputLabel>
            <Select
              multiple
              value={prefs.payType}
              disabled={!activeCell}
              displayEmpty
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <span style={{ color: "#888" }}>Any</span>
                ) : selected.filter((v) => v !== "Any").length ? (
                  options.paymentTypes
                    .filter((p) =>
                      selected
                        .filter((v) => v !== "Any")
                        .includes(String(p.value)),
                    )
                    .map((p) => p.label)
                    .join(", ")
                ) : (
                  "Any"
                )
              }
              onChange={(e) => {
                const incoming = e.target.value;
                const allPayTypes = options.paymentTypes.map((p) =>
                  String(p.value),
                );

                // If "Any" just got selected → select all
                if (
                  incoming.includes("Any") &&
                  !prefs.payType.includes("Any")
                ) {
                  update("payType", ["Any", ...allPayTypes]);
                }
                // If "Any" just got deselected → clear all
                else if (
                  !incoming.includes("Any") &&
                  prefs.payType.includes("Any")
                ) {
                  update("payType", []);
                }
                // Normal toggle
                else {
                  update(
                    "payType",
                    incoming.filter((v) => v !== "Any"),
                  );
                }
              }}
            >
              <MenuItem value="Any">
                <Checkbox
                  sx={{
                    color: "#ccc",
                    "&.Mui-checked": { color: "#90b5fe" },
                  }}
                  checked={prefs.payType.includes("Any")}
                  disabled={!activeCell}
                />
                Any
              </MenuItem>

              {options.paymentTypes.map((pt) => (
                <MenuItem key={pt.value} value={String(pt.value)}>
                  <Checkbox
                    sx={{
                      color: "#ccc",
                      "&.Mui-checked": { color: "#90b5fe" },
                    }}
                    checked={prefs.payType.includes(String(pt.value))}
                    disabled={!activeCell}
                  />
                  {pt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!activeCell}
                onChange={(e) => {
                  if (e.target.checked) {
                    setConfirmOptOutOpen(true);
                  } else {
                    setActiveCell(true);
                  }
                }}
                sx={{
                  color: "#ccc",
                  "&.Mui-checked": {
                    color: "#90b5fe",
                  },
                }}
              />
            }
            label={
              <Typography color="#fff">Opt out of Role Alert Emails</Typography>
            }
          />
        </Grid>
        {/* BUTTONS */}
        <Grid item xs={12} sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="outlined"
            type="button"
            disabled={!activeCell}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              clearForm();
            }}
          >
            CLEAR
          </Button>
          <Button
            variant="contained"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              savePrefs();
            }}
          >
            {activeCell ? "SAVE AND START GETTING CASTING ALERTS" : "SAVE"}
          </Button>
        </Grid>
      </Grid>
      <Dialog
        open={confirmOptOutOpen}
        onClose={() => setConfirmOptOutOpen(false)}
      >
        <DialogTitle>Opt Out of Role Alerts</DialogTitle>

        <DialogContent>
          <DialogContentText>
            This will permanently clear all of your Role Alert preferences. This
            action cannot be undone. If you wish to receive Role Alerts again,
            you will need to set up your preferences again.
            <br />
            <br />
            Do you want to continue?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmOptOutOpen(false)}>Cancel</Button>

          <Button
            color="error"
            onClick={() => {
              setConfirmOptOutOpen(false);
              setActiveCell(false);
              clearRoleAlertFields();
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}